'use client';

import { Layout } from '@/components/layout/Layout';
import React, { useCallback, useEffect, useState } from 'react';
import { CardProps } from '@/components/card/types';
import {
  ContentHubItemsPage,
  fetchContentHubItemsPage,
} from '@/api/content-hub';
import { fetchAllCategories } from '@/api/category';
import { Headline } from '@/components/headline/Headline';
import { Button } from '@/components/button/Button';
import ChipsContentHubWrapper from '@/components/chips/ChipsContentHub';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslations } from '@/lib';
import Card from '@/components/card/Card';

const DEFAULT_TYPES = ['artikel', 'new', 'flash-talk', 'podcast', 'edu-talk'];
const PAGE_SIZE = 12;

const getColSpanClass = (index: number): string => {
  const colSpanMap = [4, 4, 4];
  const span = colSpanMap[index % colSpanMap.length] || 4;
  return `col-span-${span}`;
};

const typeMapping: Record<string, string> = {
  new: 'news',
  artikel: 'artikel',
  'flash-talk': 'flash talk',
  'edu-talk': 'edu talk',
  podcast: 'podcast',
};

const mapToDisplayType = (apiType: string) => typeMapping[apiType] || apiType;

const mapToApiType = (displayType: string) =>
  Object.keys(typeMapping).find((key) => typeMapping[key] === displayType) ||
  displayType;

type ContentHubClientProps = {
  locale: string;
  initialContent: ContentHubItemsPage;
  initialCategories: any[];
  initialSelectedTypes: string[];
  initialSelectedCategories: string[];
};

const hasMoreContent = (content: ContentHubItemsPage) =>
  content.pagination
    ? (content.pagination.hasMore ??
      content.pagination.start + content.items.length <
        content.pagination.total)
    : content.items.length === PAGE_SIZE;

export default function ContentHubClient({
  locale,
  initialContent,
  initialCategories,
  initialSelectedTypes,
  initialSelectedCategories,
}: ContentHubClientProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { t } = useTranslations();

  const [filteredContent, setFilteredContent] = useState<CardProps[]>(
    initialContent.items,
  );
  const [categories, setCategories] = useState<any[]>(initialCategories);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(
    initialSelectedTypes.map(mapToDisplayType),
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    initialSelectedCategories,
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(
    hasMoreContent(initialContent),
  );
  const linkTitle = t('smzhub.readMore') || 'Mehr lesen';
  const artikelTranslation = t('smzhub.artikel') || 'Artikel';

  // Function to fetch content
  const fetchFilteredContent = useCallback(
    async (types: string[], categories: string[], page: number = 1) => {
      try {
        const apiTypes = types.map(mapToApiType);

        const response = await fetchContentHubItemsPage(
          locale,
          apiTypes,
          categories,
          page,
          PAGE_SIZE,
          linkTitle,
          artikelTranslation,
        );
        setFilteredContent((prevContent) =>
          page === 1 ? response.items : [...prevContent, ...response.items],
        );

        setHasMore(
          response.pagination
            ? (response.pagination.hasMore ??
                response.pagination.start + response.items.length <
                  response.pagination.total)
            : response.items.length === PAGE_SIZE,
        );
      } catch (error) {
        console.error('Error fetching content:', error);
        setFilteredContent([]);
        setHasMore(false);
      }
    },
    [artikelTranslation, linkTitle, locale],
  );

  useEffect(() => {
    setFilteredContent(initialContent.items);
    setHasMore(hasMoreContent(initialContent));
    setSelectedTypes(initialSelectedTypes.map(mapToDisplayType));
    setSelectedCategories(initialSelectedCategories);
    setCurrentPage(1);
  }, [initialContent, initialSelectedCategories, initialSelectedTypes]);

  useEffect(() => {
    if (initialCategories.length > 0) {
      return;
    }

    let cancelled = false;

    async function loadCategories() {
      const categoryData = await fetchAllCategories(locale);

      if (!cancelled) {
        setCategories(categoryData);
      }
    }

    loadCategories();

    return () => {
      cancelled = true;
    };
  }, [initialCategories.length, locale]);

  const updateQueryParams = (types: string[], categories: string[]) => {
    const query = new URLSearchParams();

    if (types.length > 0) query.set('types', types.map(mapToApiType).join(','));
    if (categories.length > 0) query.set('categories', categories.join(','));

    const queryString = query.toString();
    router.replace(queryString ? `${pathname}?${queryString}` : pathname, {
      scroll: false,
    });
  };

  // Handle type filter change
  const handleTypeSelectionChange = (selected: string[]) => {
    setSelectedTypes(selected);
    setCurrentPage(1); // Reset pagination
    updateQueryParams(selected, selectedCategories);
  };

  // Handle category filter change
  const handleCategorySelectionChange = (selected: string[]) => {
    setSelectedCategories(selected);
    setCurrentPage(1); // Reset pagination
    updateQueryParams(selectedTypes, selected);
  };

  // Handle "Load More" functionality
  const loadMoreContent = async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);

    try {
      const nextPage = currentPage + 1;

      await fetchFilteredContent(
        selectedTypes.length > 0 ? selectedTypes : DEFAULT_TYPES,
        selectedCategories,
        nextPage,
      );

      setCurrentPage(nextPage);
    } catch (error) {
      console.error('Error loading more content:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Clear all filters
  const handleClearAll = () => {
    setSelectedTypes([]);
    setSelectedCategories([]);
    setCurrentPage(1);
    updateQueryParams([], []);
  };

  return (
    <Layout variant="guttersOnly" className="border-t border-neutral-50">
      <div className="content-hub container mx-auto flex min-h-screen flex-col gap-14 overflow-hidden pb-24 pt-12">
        <div className="flex flex-col gap-6">
          <div className="flex flex-row justify-between">
            <Headline
              title="Filter"
              level="h1"
              variant="headingTwo"
              className="text-brand-700"
            />
            <button
              onClick={handleClearAll}
              className="flex flex-row items-center gap-2 rounded-lg border-none bg-transparent !p-0.5 px-[18px] py-3 !text-brand-700 hover:text-neutral-700 hover:underline hover:underline-offset-4 disabled:text-neutral-300 disabled:no-underline"
            >
              {t('smzhub.deleteFilter')}
            </button>
          </div>
          <div className="chips-holder flex flex-col gap-6">
            <ChipsContentHubWrapper
              options={['artikel', 'news', 'flash talk', 'edu talk', 'podcast']}
              selectedOptions={selectedTypes}
              onSelectionChange={handleTypeSelectionChange}
              selectionMode="multiple"
            />
            <ChipsContentHubWrapper
              options={categories.map((category: any) => category.title)}
              selectedOptions={selectedCategories}
              onSelectionChange={handleCategorySelectionChange}
              selectionMode="multiple"
            />
          </div>
        </div>
        {filteredContent.length === 0 ? (
          <div className="flex min-h-64 items-center justify-center rounded border border-neutral-100 px-6 py-12 text-center text-brand-700">
            {t('smzhub.noResults')}
          </div>
        ) : (
          <div className="grid auto-rows-auto gap-4 overflow-hidden sm:grid-cols-4 lg:grid-cols-12">
            {filteredContent.map((card: CardProps, index: number) => (
              <div
                key={`${card.link?.href ?? card.title ?? 'card'}-${index}`}
                className={`break-inside-avoid ${getColSpanClass(index)} block h-full`}
              >
                <Card {...card} className="!w-max-full" />
              </div>
            ))}
          </div>
        )}

        {isLoadingMore && (
          <span className="flex justify-center rounded px-4 py-2 text-brand-700">
            <svg
              className="mr-3 h-5 w-5 animate-spin text-brand-700"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C3.58 0 0 5.82 0 12h4z"
              ></path>
            </svg>
            Loading...
          </span>
        )}
        {filteredContent.length > 0 && !isLoadingMore && hasMore && (
          <div className="flex justify-center">
            <Button
              variant="secondary"
              onClick={loadMoreContent}
              rightIcon={{ name: 'refresh-cw' }}
              name={t('smzhub.showMore')}
            />
          </div>
        )}
      </div>
    </Layout>
  );
}
